import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useAuthenticatedUser } from "@/queries/user-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import {
    CreateIntegrationRequest,
    UpdateIntegrationRequest,
    DeleteIntegrationRequest,
} from "gitpod-next-api/gitpod/v1/integration_pb";

// Query key factories
export const integrationDefinitionsQueryKey = () => ["integration-definitions"];
export const integrationsQueryKey = (organizationId?: string) => ["integrations", organizationId].filter(Boolean);

// List integration definitions
export const useListIntegrationDefinitions = () => {
    const api = useGitpodAPI();
    return useQuery({
        queryKey: integrationDefinitionsQueryKey(),
        queryFn: async () => {
            const response = await api.integrationService.listIntegrationDefinitions({});
            return response.definitions;
        },
    });
};

// List integrations for organization
export const useListIntegrations = (organizationId?: string) => {
    const api = useGitpodAPI();
    return useQuery({
        queryKey: integrationsQueryKey(organizationId),
        queryFn: async () => {
            if (!organizationId) {
                return [];
            }
            const response = await api.integrationService.listIntegrations({});
            return response.integrations;
        },
        enabled: !!organizationId,
    });
};

// Create integration mutation
export const useCreateIntegration = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (request: PlainMessage<CreateIntegrationRequest>) => {
            const response = await api.integrationService.createIntegration(new CreateIntegrationRequest(request));
            return response.integration;
        },
        onSuccess: () => {
            // Invalidate integrations list
            void queryClient.invalidateQueries({
                queryKey: integrationsQueryKey(user?.organizationId),
            });
        },
    });
};

// Update integration mutation
export const useUpdateIntegration = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (request: PlainMessage<UpdateIntegrationRequest>) => {
            const response = await api.integrationService.updateIntegration(new UpdateIntegrationRequest(request));
            return response.integration;
        },
        onSuccess: () => {
            // Invalidate integrations list
            void queryClient.invalidateQueries({
                queryKey: integrationsQueryKey(user?.organizationId),
            });
        },
    });
};

// Delete integration mutation
export const useDeleteIntegration = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (request: PlainMessage<DeleteIntegrationRequest>) => {
            await api.integrationService.deleteIntegration(new DeleteIntegrationRequest(request));
        },
        onSuccess: () => {
            // Invalidate integrations list
            void queryClient.invalidateQueries({
                queryKey: integrationsQueryKey(user?.organizationId),
            });
        },
    });
};
